import { Box, Checkbox, CheckboxGroup, Flex, Stack, useToast } from "@chakra-ui/react";
import { ResponsiveBar } from "@nivo/bar";
import { useGetSuppliersYearByYearData, useGetSuppliersYearlyData } from "../../../../hooks/query";
import {
  constructQueryParamsString,
  getFormattedNumValue,
  handlePageSizeChange,
  showErrorToast,
} from "../../../../utils";
import { hideAppSpinner } from "../../../../context/Store/slices/appSpinnerSlice";
import { useDispatch } from "react-redux";
import { useMemo, useState } from "react";
import * as React from "react";
import { MultiSelect } from "../../../../components";
import { getFilteredData, transformBarData } from "../../utils";
import EmissionFilterComponent from "../EmissionFilterComponent";
import LocalSpinner from "../../../../components/LocalSpinner";

const DEFAULT_YEARS = ["2021", "2022", "2023", "2024"];

const dummy = [
  {
    id: "PKF Global BV (Integra) • 2021",
    supplier: "PKF Global BV (Integra)",
    year: "2021",
    scope1: 10000,
    scope2: 12000,
    scope3: 13770,
  },
  {
    id: "PKF Global BV (Integra) • 2022",
    supplier: "PKF Global BV (Integra)",
    year: "2022",
    scope1: 4000,
    scope2: 3800,
    scope3: 3513,
  },
  {
    id: "Attributes Supplier • 2021",
    supplier: "Attributes Supplier",
    year: "2021",
    scope1: 1324.43,
    scope2: 1500,
    scope3: 1500,
  },
];

const keys = ["scope1", "scope2", "scope3"];

const EmissionsGraphTab = () => {
  const toast = useToast();
  const dispatch = useDispatch();

  const [emissionsData, setEmissionsData] = useState([]);
  const [selectedDataQualityFilters, setSelectedDataQualityFilters] = useState([]);
  const [selectedScopeFilters, setSelectedScopeFilters] = useState([]);
  const [years, setYears] = useState(DEFAULT_YEARS);

  const getQueryParamsString = () => {
    const queryParams = {
      years: years.length !== 0 ? years : DEFAULT_YEARS,
    };
    return constructQueryParamsString(queryParams);
  };

  const { isFetching } = useGetSuppliersYearlyData(getQueryParamsString(), {
    onSuccess: (data) => {
      // exclude data where excludeFromReport is true
      const filteredData = data.filter((item) => !item.excludeFromReport);
      setEmissionsData(filteredData);
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner()),
    cacheTime: 0,
  });

  const chartHeight = Math.max(600, emissionsData.length * 30); // min 200px

  const filteredData = useMemo(() => {
    const filtered = getFilteredData(emissionsData, selectedDataQualityFilters);
    return transformBarData(filtered, selectedScopeFilters, years);
  }, [emissionsData, selectedDataQualityFilters, selectedScopeFilters, years]);

  if (isFetching) {
    return <LocalSpinner my={8} />;
  }

  return (
    <Flex display={"block"} height="100%" width={"100%"}>
      <Flex direction="column" gap={4} mt={8}>
        <Flex align="center" gap={4}>
          <Box as="label" fontWeight="bold" fontSize="sm" color="gray.600">
            Year:
          </Box>
          <Box minW="250px">
            <MultiSelect
              onChange={(value) => setYears(value.map((item) => item.value))}
              options={Array.from({ length: 10 }, (_, i) => {
                const year = new Date().getFullYear() + 1 - i;
                return { label: year.toString(), value: year.toString() };
              })}
              isMulti={true}
              defaultValue={years.map((year) => ({ label: year, value: year }))}
            />
          </Box>
        </Flex>
        <EmissionFilterComponent
          type="dataQuality"
          selectedValues={selectedDataQualityFilters}
          onChange={(e) => setSelectedDataQualityFilters(e)}
        />
        <EmissionFilterComponent
          type="scope"
          selectedValues={selectedScopeFilters}
          onChange={(e) => setSelectedScopeFilters(e)}
        />
      </Flex>

      <Box flex={1} height={`${chartHeight}px`}>
        <ResponsiveBar
          data={filteredData}
          keys={years}
          indexBy="supplier"
          layout="horizontal"
          groupMode="grouped"
          margin={{ top: 50, right: 130, bottom: 50, left: 200 }}
          padding={0.3}
          valueScale={{ type: "linear" }}
          indexScale={{ type: "band", round: true }}
          colors={{ scheme: "nivo" }} // or create a custom color scheme
          borderColor={{ from: "color", modifiers: [["darker", 1.6]] }}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: "tCO2e",
            legendPosition: "middle",
            legendOffset: 32,
          }}
          labelSkipWidth={12}
          labelSkipHeight={12}
          labelTextColor={{ from: "color", modifiers: [["darker", 1.6]] }}
          legends={[
            {
              dataFrom: "keys",
              anchor: "bottom-right",
              direction: "column",
              justify: false,
              translateX: 120,
              translateY: 0,
              itemsSpacing: 2,
              itemWidth: 100,
              itemHeight: 20,
              itemDirection: "left-to-right",
              itemOpacity: 0.85,
              symbolSize: 20,
              effects: [
                {
                  on: "hover",
                  style: {
                    itemOpacity: 1,
                  },
                },
              ],
            },
          ]}
          animate={true}
          motionConfig="wobbly"
          valueFormat={(value) => `${getFormattedNumValue(value)} tCO2e`}
        />
      </Box>
    </Flex>
  );
};

export default EmissionsGraphTab;
